import React, { useCallback, useEffect, useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PersonRounded from "@mui/icons-material/PersonRounded";
import Button from "../../../Button";
import WaitlistAPI, {
    WaitlistUser,
    WaitlistEntry,
} from "../../../../api/Waitlist";
import EmptyListState from "../../../EmptyListState";
import DateTimePicker from "../../../Modals/DateTimePicker";
import { Dayjs } from "dayjs";
import { FONTS } from "../../../../constants/styles";

type WaitlistProps =
    | {
          accountKey: string;
          brandKey: string;
          mode?: "default";
          onSelect: (entry: WaitlistSelection) => void;
      }
    | {
          accountKey: string;
          brandKey: string;
          mode: "datetime";
          onSelect: (
              entry: WaitlistSelection
          ) => (
              dateTime: Dayjs,
              close: () => void,
              setLoading: (val: boolean) => void
          ) => void;
      };

export type WaitlistSelection = {
    userKey: string;
    bookingKey: string;
    serviceKey: string;
    serviceDuration: number;
};

const Waitlist = ({
    accountKey,
    brandKey,
    onSelect,
    mode = "default",
}: WaitlistProps) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const [waitlistUsers, setWaitlistUsers] = useState<WaitlistUser[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchWaitlist = useCallback(() => {
        setLoading(true);
        WaitlistAPI.usersList(accountKey, brandKey)
            .then((res) => setWaitlistUsers(res ?? []))
            .catch((error) => {
                console.error("Failed to load waitlist:", error);
            })
            .finally(() => setLoading(false));
    }, [accountKey, brandKey]);

    useEffect(() => {
        fetchWaitlist();
    }, [fetchWaitlist]);

    if (!loading && waitlistUsers.length === 0) {
        return (
            <Box sx={{ pt: 4 }}>
                <EmptyListState primaryText="No users on the waitlist" />
            </Box>
        );
    }

    let staggerIndex = 0;

    return (
        <Stack
            spacing={3}
            className="force-dynamic-translation"
            sx={{
                px: 2,
                pb: "200px",
                "@keyframes fadeSlideIn": {
                    from: {
                        opacity: 0,
                        transform: "translateY(12px)",
                    },
                    to: {
                        opacity: 1,
                        transform: "translateY(0)",
                    },
                },
                animation: "fadeSlideIn 0.3s ease-out",
            }}
        >
            {waitlistUsers.map((user) => (
                <Stack key={user.userKey} spacing={0.5}>
                    {/* Group header — matches alphabet header */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ px: 0.5, mb: 1 }}
                    >
                        <Typography
                            fontFamily={FONTS.display}
                            fontWeight={700}
                            fontSize="14px"
                            color={primary}
                            noWrap
                            sx={{ flexShrink: 0 }}
                        >
                            {user.userName}
                        </Typography>
                        {user.hasPriority && (
                            <Box sx={{ px: 0.75, py: 0.125, borderRadius: "4px", bgcolor: alpha(primary, 0.1) }}>
                                <Typography fontSize="10px" fontWeight={700} color={primary} textTransform="uppercase">
                                    Priority
                                </Typography>
                            </Box>
                        )}
                        <Box
                            flex={1}
                            height="1px"
                            bgcolor={alpha(primary, 0.1)}
                        />
                    </Stack>

                    {/* Entry rows */}
                    {user.waitlistEntries.map(
                        (entry: WaitlistEntry, i: number) => {
                            const currentIndex = staggerIndex++;
                            const entryData = {
                                userKey: user.userKey,
                                bookingKey: entry.bookingKey,
                                bookingNote: entry.bookingNote,
                                serviceKey: entry.serviceKey,
                                serviceDuration: entry.serviceDuration,
                            };

                            return (
                                <React.Fragment key={entry.bookingKey}>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        sx={{
                                            py: 1,
                                            px: 0.5,
                                            borderRadius: "12px",
                                            "@keyframes fadeSlideUp": {
                                                from: {
                                                    opacity: 0,
                                                    transform:
                                                        "translateY(10px)",
                                                },
                                                to: {
                                                    opacity: 1,
                                                    transform:
                                                        "translateY(0)",
                                                },
                                            },
                                            animation: `fadeSlideUp 0.25s ease-out ${currentIndex * 30}ms both`,
                                        }}
                                    >
                                        {/* 44px rounded-square avatar */}
                                        <Box
                                            sx={{
                                                width: 44,
                                                minWidth: 44,
                                                height: 44,
                                                borderRadius: "14px",
                                                backgroundImage: user.userPhoto
                                                    ? `url(${user.userPhoto})`
                                                    : undefined,
                                                backgroundSize: "cover",
                                                backgroundPosition: "center",
                                                bgcolor: !user.userPhoto
                                                    ? alpha(primary, 0.1)
                                                    : undefined,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flex: "0 0 auto",
                                            }}
                                        >
                                            {!user.userPhoto && (
                                                <PersonRounded
                                                    sx={{
                                                        fontSize: 22,
                                                        color: primary,
                                                        opacity: 0.6,
                                                    }}
                                                />
                                            )}
                                        </Box>

                                        {/* Info */}
                                        <Stack
                                            flex={1}
                                            ml={1.5}
                                            spacing={0.25}
                                            sx={{ overflow: "hidden" }}
                                        >
                                            <Typography
                                                fontFamily={FONTS.body}
                                                fontWeight={600}
                                                fontSize="15px"
                                                lineHeight={1.3}
                                                noWrap
                                            >
                                                {entry.serviceName}
                                            </Typography>
                                            {entry.bookingNote && (
                                                <Typography
                                                    fontSize="12px"
                                                    color="text.secondary"
                                                    fontStyle="italic"
                                                    noWrap
                                                >
                                                    &ldquo;
                                                    {entry.bookingNote}
                                                    &rdquo;
                                                </Typography>
                                            )}
                                        </Stack>

                                        {/* Schedule action */}
                                        <Box
                                            sx={{ ml: 1, flexShrink: 0 }}
                                        >
                                            {mode === "default" ? (
                                                <Button
                                                    variant="small"
                                                    onClick={() =>
                                                        onSelect(entryData)
                                                    }
                                                >
                                                    Schedule
                                                </Button>
                                            ) : (
                                                <DateTimePicker
                                                    duration={
                                                        entryData.serviceDuration
                                                    }
                                                    button={{
                                                        variant: "small",
                                                        color: "primary",
                                                    }}
                                                    onSave={(
                                                        dateTime,
                                                        close,
                                                        setLoading
                                                    ) => {
                                                        const handler =
                                                            onSelect(
                                                                entryData
                                                            );
                                                        if (
                                                            typeof handler ===
                                                            "function"
                                                        ) {
                                                            handler(
                                                                dateTime,
                                                                () => {
                                                                    close();
                                                                    fetchWaitlist();
                                                                },
                                                                setLoading
                                                            );
                                                        } else {
                                                            close();
                                                        }
                                                    }}
                                                    onCancel={() => {}}
                                                >
                                                    Schedule
                                                </DateTimePicker>
                                            )}
                                        </Box>
                                    </Stack>

                                    {/* Divider between entries */}
                                    {i <
                                        user.waitlistEntries.length - 1 && (
                                        <Box
                                            sx={{
                                                height: "1px",
                                                bgcolor: "rgba(0,0,0,0.06)",
                                                ml: "60px",
                                            }}
                                        />
                                    )}
                                </React.Fragment>
                            );
                        }
                    )}
                </Stack>
            ))}
        </Stack>
    );
};

export default Waitlist;
